import React from 'react';
import { FileText, Clock, Shield, CheckCircle, AlertCircle, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import StaticContentHelper from '../components/StaticContentHelper';
import Breadcrumbs from '../components/Breadcrumbs';

const CourtFilingPage = () => {
  const serviceData = [
    {
      name: 'E-Filing Services',
      description: '24/7 electronic filing availability with instant confirmation',
    },
    {
      name: 'Traditional Court Filing',
      description: 'In-person courthouse filing with direct clerk interaction',
    },
    {
      name: 'Same-Day Filing',
      description: 'Emergency filing for critical deadlines',
    }
  ];

  const cities = [
    'Palm Springs', 'Riverside', 'Temecula', 'Palm Desert', 'Indio', 'Coachella',
    'La Quinta', 'Rancho Mirage', 'Cathedral City', 'Desert Hot Springs',
    'Indian Wells', 'Thousand Palms', 'Twentynine Palms', 'Yucca Valley',
    'Joshua Tree', 'Morongo Valley', 'Landers', 'Moreno Valley', 'Corona',
    'Menifee', 'Murrieta'
  ];

  const courtFilingSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Court Filing Services California",
    "description": "Professional court filing services throughout California. E-filing and traditional filing for all California courts. Same-day filing available for urgent matters.",
    "provider": {
      "@type": "LegalService",
      "name": "Legal Document Specialist",
      "telephone": "(760) 866-9182",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "225 South Civic Drive, Ste 2-16",
        "addressLocality": "Palm Springs",
        "addressRegion": "CA",
        "postalCode": "92262",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "California"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "availableLanguage": "English",
      "servicePhone": "(760) 866-9182",
      "serviceUrl": `${window.location.origin}/court-filing`
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "E-Filing Services",
        "description": "Electronic court filing with instant confirmation",
      },
      {
        "@type": "Offer",
        "name": "Traditional Court Filing",
        "description": "In-person courthouse filing services",
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is e-filing mandatory in California courts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "As of 2025, major California courts including Los Angeles, Orange, San Diego, Santa Clara, and Solano require mandatory e-filing for attorneys in civil, family, and probate matters. The California Supreme Court and Courts of Appeal require e-filing for all litigants with limited exceptions. Some courts still allow permissive e-filing, while a few rural counties remain paper-only. Federal district courts mandate e-filing through CM/ECF."
        }
      },
      {
        "@type": "Question",
        "name": "What are the benefits of e-filing versus traditional filing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "E-filing offers 24/7 filing availability, instant confirmation receipts, faster processing times, reduced court clerk interaction, electronic case management, and same-day filing capability. Filings between 12:00 a.m. and 11:59:59 p.m. on court days are deemed filed that day. Electronic service also provides two additional court days to respond compared to traditional mail or fax service."
        }
      },
      {
        "@type": "Question",
        "name": "How much does court filing cost in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Court filing costs include court filing fees plus our service fees, which vary based on document type, urgency, and court location. Email your case specifics to quote@legaldocspecialist.com for a quote. Court fees are additional to service fees."
        }
      },
      {
        "@type": "Question",
        "name": "What file formats are accepted for e-filing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "California courts typically accept PDF format for e-filed documents. Files are often limited to 25MB per file and 35MB per envelope. Documents must be legible, properly formatted, and meet court-specific requirements. Some courts have additional format restrictions, so it's important to check local rules before filing."
        }
      },
      {
        "@type": "Question",
        "name": "Can I e-file in all California courts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most California superior courts, all appellate courts, and the Supreme Court accept e-filing. Major courts have mandatory e-filing requirements. However, some rural counties like Glenn, Modoc, and Sierra remain paper-only. Court-specific capabilities vary, so it's important to verify e-filing availability for your jurisdiction before submission."
        }
      },
      {
        "@type": "Question",
        "name": "What happens if I miss a court filing deadline?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Missing court filing deadlines can result in rejected documents, case delays, sanctions, or adverse rulings. Each court enforces strict deadlines for paying fees, uploading documents, and responding to filings. For e-filed documents, any filing received between 12:00 a.m. and 11:59:59 p.m. on a court day is deemed filed that day. We offer same-day filing services for urgent deadline situations."
        }
      }
    ]
  };

  React.useEffect(() => {
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.textContent = JSON.stringify(courtFilingSchema);
    document.head.appendChild(serviceScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(courtFilingSchema) || 
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="Court Filing Services California | E-Filing & Traditional Filing | Legal Document Specialist"
        description="Professional court filing services throughout California. E-filing and traditional filing for all California courts. Same-day filing available. Request a quote online."
        keywords="court filing California, e-filing California courts, legal document filing, California court documents, same day court filing, electronic filing"
        canonical="/court-filing"
      />
      <StaticContentHelper 
        pageName="Court Filing Services"
        services={serviceData}
        cities={cities}
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              California Court Filing Services
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional court filing services throughout California with e-filing and traditional filing options. Same-day filing available for urgent matters.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/quote?service=court-filing" className="btn-primary" data-testid="hero-quote-link">
                Get Court Filing Quote
              </Link>
              <a href="mailto:support@legaldocspecialist.com" className="btn-secondary">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* California E-Filing Landscape 2025 */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              California E-Filing Landscape 2025
            </h2>
            
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2rem',
              textAlign: 'center',
              lineHeight: '1.8'
            }}>
              California's electronic filing system continues to evolve with significant expansion of mandatory e-filing in both state superior and appellate courts. The landscape is shaped by statewide rules, local court adaptations, and advanced technology requirements.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              marginTop: '3rem'
            }}>
              {[
                {
                  title: 'Mandatory E-Filing Courts',
                  description: 'Major courts including Los Angeles, Orange, San Diego, Santa Clara, and Solano require attorneys to e-file in civil, family, and probate matters. California Supreme Court and Courts of Appeal require e-filing for all litigants.'
                },
                {
                  title: 'Permissive E-Filing Courts',
                  description: 'Some courts like Contra Costa and San Mateo allow but don\'t require e-filing for most case types. Attorneys have the option to file electronically or traditionally based on preference.'
                },
                {
                  title: 'Federal Court Requirements',
                  description: 'All California federal district courts mandate e-filing through the federal CM/ECF system. Attorneys must be registered and trained on the CM/ECF platform.'
                },
                {
                  title: 'Paper-Only Counties',
                  description: 'A few rural counties including Glenn, Modoc, and Sierra remain paper-only, requiring traditional in-person or mail filing for all documents.'
                }
              ].map((item, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-page)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {item.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* E-Filing vs Traditional */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              E-Filing vs Traditional Filing
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Understanding the differences between electronic and traditional court filing
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem'
          }}>
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '2px solid var(--brand-primary)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', color: 'var(--brand-primary)' }}>
                E-Filing Services
              </h3>
              <p className="body-small" style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                lineHeight: '1.6'
              }}>
                Modern electronic filing offers superior convenience and efficiency for today's legal professionals.
              </p>
              <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                {[
                  '24/7 filing availability from anywhere',
                  'Instant confirmation receipts',
                  'Faster processing times',
                  'Reduced court clerk interaction',
                  'Electronic case management integration',
                  'Same-day filing capability',
                  'Automatic PDF conversion',
                  'Real-time tracking and status updates',
                  'Two extra days for electronic service responses',
                  'Secure encrypted transmission'
                ].map((feature, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <CheckCircle size={18} color="var(--brand-primary)" />
                    <span style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Traditional Filing
              </h3>
              <p className="body-small" style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                lineHeight: '1.6'
              }}>
                Traditional paper filing remains necessary for certain courts and document types.
              </p>
              <ul style={{ paddingLeft: '0', listStyle: 'none' }}>
                {[
                  'In-person courthouse filing required',
                  'Physical document handling and printing',
                  'Direct clerk interaction',
                  'Paper copy management',
                  'Manual processing delays',
                  'Traditional court procedures',
                  'Limited to court business hours',
                  'Travel to courthouse necessary',
                  'Original documents for certain filings',
                  'Courtesy copies may still be required'
                ].map((feature, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    <CheckCircle size={18} color="var(--text-secondary)" />
                    <span style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Filing Deadlines and Rules */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Critical Deadlines & Filing Rules
            </h2>
            
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                <AlertCircle size={24} color="var(--brand-primary)" style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
                E-Filing Deadline Calculations
              </h3>
              <p className="body-medium" style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                lineHeight: '1.8'
              }}>
                For e-filed documents, any filing received electronically between 12:00 a.m. and 11:59:59 p.m. on a court day is deemed filed that day. Filings received on non-court days (weekends/holidays) are deemed filed on the next court day.
              </p>
              <p className="body-medium" style={{ 
                color: 'var(--text-secondary)',
                lineHeight: '1.8'
              }}>
                <strong>Electronic Service Advantage:</strong> When the court or a party electronically serves a document, recipients receive two additional court days to respond compared to traditional mail or fax service. This extension applies to court orders, rulings, and notices served electronically.
              </p>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Key E-Filing Requirements for Attorneys
              </h3>
              <ul style={{
                paddingLeft: '0',
                listStyle: 'none'
              }}>
                {[
                  'Attorneys must accept electronic service for court notices starting July 1, 2025 in all civil and family matters',
                  'Email addresses must be kept current with the court using Judicial Council forms',
                  'Court e-service is legally equivalent to traditional service methods (mail, fax, overnight delivery)',
                  'Use only court-approved electronic filing service providers (EFSPs)',
                  'Files typically limited to PDF format, often 25MB per file and 35MB per envelope',
                  'Self-represented parties may opt into electronic service but are not required',
                  'Some documents (original wills, certain probate filings) cannot be e-filed in some courts',
                  'Each court enforces its own deadlines for fees, uploads, and responses'
                ].map((rule, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <Shield size={20} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices for Law Firms */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Best Practices for Law Firms in 2025
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              marginTop: '3rem'
            }}>
              {[
                {
                  icon: <Users size={24} />,
                  title: 'Keep Contact Info Current',
                  description: 'Ensure all attorney contact details, especially email addresses, are current with every court. Update information immediately when changes occur.'
                },
                {
                  icon: <FileText size={24} />,
                  title: 'Know Local Rules',
                  description: 'E-filing requirements, accepted documents, and courtesy copy rules vary by county and case type. Regularly check court websites for updates.'
                },
                {
                  icon: <Shield size={24} />,
                  title: 'Use Vetted EFSPs',
                  description: 'Use court-approved electronic filing and e-service providers for secure, compliant, and efficient submissions with proper encryption.'
                },
                {
                  icon: <Users size={24} />,
                  title: 'Train Your Team',
                  description: 'Educate all team members on e-filing procedures, deadline calculations, troubleshooting, and security protocols for consistent compliance.'
                },
                {
                  icon: <Clock size={24} />,
                  title: 'Monitor Deadlines',
                  description: 'Use calendaring software that accounts for e-service extensions and court holidays. Set reminders well in advance of critical deadlines.'
                },
                {
                  icon: <TrendingUp size={24} />,
                  title: 'Verify Acceptances',
                  description: 'Monitor court confirmation emails or online dashboards to confirm filing acceptance. Never assume a filing is accepted without verification.'
                }
              ].map((practice, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    color: 'white'
                  }}>
                    {practice.icon}
                  </div>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {practice.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {practice.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '60px 0' }}>
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '3rem',
            borderRadius: '12px',
            border: '2px solid var(--brand-primary)',
            textAlign: 'center'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
              Understanding California Court Procedures
            </h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.8' }}>
              Explore our comprehensive guide to California court filing deadlines, service requirements, motion rules, and critical 2025 legislative updates.
            </p>
            <Link 
              to="/resources/california-court-procedures-guide"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1rem 2rem',
                backgroundColor: 'var(--brand-primary)',
                color: 'white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Read California Court Procedures Guide →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'Is e-filing mandatory in California courts?',
                answer: 'As of 2025, major California courts including Los Angeles, Orange, San Diego, Santa Clara, and Solano require mandatory e-filing for attorneys in civil, family, and probate matters. The California Supreme Court and Courts of Appeal require e-filing for all litigants with limited exceptions. Some courts still allow permissive e-filing, while a few rural counties remain paper-only. Federal district courts mandate e-filing through CM/ECF.'
              },
              {
                question: 'What are the benefits of e-filing versus traditional filing?',
                answer: 'E-filing offers 24/7 filing availability, instant confirmation receipts, faster processing times, reduced court clerk interaction, electronic case management, and same-day filing capability. Filings between 12:00 a.m. and 11:59:59 p.m. on court days are deemed filed that day. Electronic service also provides two additional court days to respond compared to traditional mail or fax service.'
              },
              {
                question: 'How much does court filing cost in California?',
                answer: 'Court filing costs include court filing fees plus our service fees, which vary based on document type, urgency, and court location. Email your case specifics to quote@legaldocspecialist.com for a quote. Court fees are additional to service fees.'
              },
              {
                question: 'What file formats are accepted for e-filing?',
                answer: 'California courts typically accept PDF format for e-filed documents. Files are often limited to 25MB per file and 35MB per envelope. Documents must be legible, properly formatted, and meet court-specific requirements. Some courts have additional format restrictions, so it\'s important to check local rules before filing.'
              },
              {
                question: 'Can I e-file in all California courts?',
                answer: 'Most California superior courts, all appellate courts, and the Supreme Court accept e-filing. Major courts have mandatory e-filing requirements. However, some rural counties like Glenn, Modoc, and Sierra remain paper-only. Court-specific capabilities vary, so it\'s important to verify e-filing availability for your jurisdiction before submission.'
              },
              {
                question: 'What happens if I miss a court filing deadline?',
                answer: 'Missing court filing deadlines can result in rejected documents, case delays, sanctions, or adverse rulings. Each court enforces strict deadlines for paying fees, uploading documents, and responding to filings. For e-filed documents, any filing received between 12:00 a.m. and 11:59:59 p.m. on a court day is deemed filed that day. We offer same-day filing services for urgent deadline situations.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Need Court Filing Services?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Professional e-filing and traditional court filing throughout California
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/place-order"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Place Order Now
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CourtFilingPage;
